import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { CheckCircle, Wine, Star, Share2, Download, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { tastingApi } from '@/api/services';
import { useTastingStore } from '@/stores/tastingStore';
import { useEventStore } from '@/stores/eventStore';
import TastingLayout from '@/components/layout/TastingLayout';

export default function CompletedPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { event } = useEventStore();
  const { spirits, reset } = useTastingStore();

  // Fetch final session data
  const { data: session, isLoading } = useQuery({
    queryKey: ['session-completed', sessionId],
    queryFn: () => tastingApi.getSession(sessionId!),
    enabled: !!sessionId,
  });

  // Calculate average rating
  const averageRating = spirits.length > 0
    ? spirits.reduce((sum, s) => sum + (s.overall_rating || 0), 0) / spirits.length
    : 0;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Mein Tasting-Ergebnis',
          text: `Ich habe gerade ein Tasting mit ${spirits.length} Spirituosen abgeschlossen!`,
          url: window.location.href,
        });
      } catch {
        // User cancelled or share failed
      }
    } else {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link kopiert!');
    }
  };

  if (isLoading) {
    return (
      <TastingLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      </TastingLayout>
    );
  }

  return (
    <TastingLayout showBranding={!!event?.branding} branding={event?.branding}>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Success Header */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center mb-8"
        >
          <div className="w-20 h-20 bg-success-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-success-500" />
          </div>

          <h1 className="text-3xl md:text-4xl font-display font-bold text-dark-50 mb-4">
            Tasting abgeschlossen!
          </h1>

          <p className="text-dark-400 max-w-md mx-auto">
            Du hast {spirits.length} Spirituose{spirits.length !== 1 ? 'n' : ''} verkostet.
            Hier ist deine Zusammenfassung.
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-4 mb-8"
        >
          <div className="card text-center">
            <Wine className="w-6 h-6 text-primary-500 mx-auto mb-2" />
            <div className="text-2xl font-bold text-dark-50">{spirits.length}</div>
            <div className="text-sm text-dark-400">Spirituosen</div>
          </div>
          <div className="card text-center">
            <Star className="w-6 h-6 text-accent-500 mx-auto mb-2" />
            <div className="text-2xl font-bold text-dark-50">{averageRating.toFixed(1)}</div>
            <div className="text-sm text-dark-400">Durchschnitt</div>
          </div>
          <div className="card text-center">
            <CheckCircle className="w-6 h-6 text-success-500 mx-auto mb-2" />
            <div className="text-2xl font-bold text-dark-50">100%</div>
            <div className="text-sm text-dark-400">Abgeschlossen</div>
          </div>
        </motion.div>

        {/* Spirit Results */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card mb-8"
        >
          <h2 className="text-lg font-semibold text-dark-100 mb-4">Deine Bewertungen</h2>
          <div className="space-y-4">
            {spirits.map((spirit, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-dark-800/50 rounded-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 font-medium">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-medium text-dark-100">{spirit.spirit_name}</div>
                    {spirit.spirit_brand && (
                      <div className="text-sm text-dark-400">{spirit.spirit_brand}</div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-accent-500" />
                  <span className="text-lg font-semibold text-dark-100">
                    {spirit.overall_rating || '-'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Outro Text */}
        {event?.branding?.outro_text && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card mb-8 text-center"
          >
            <p className="text-dark-300">{event.branding.outro_text}</p>
          </motion.div>
        )}

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-3"
        >
          <button
            onClick={handleShare}
            className="btn-secondary flex-1 flex items-center justify-center gap-2"
          >
            <Share2 className="w-5 h-5" />
            Teilen
          </button>
          <button
            onClick={() => toast.success('Feature kommt bald!')}
            className="btn-secondary flex-1 flex items-center justify-center gap-2"
          >
            <Download className="w-5 h-5" />
            PDF Export
          </button>
        </motion.div>

        {/* Thank You */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-12 text-dark-500 text-sm"
        >
          <p>Vielen Dank für deine Teilnahme!</p>
          <p className="mt-1">Wir freuen uns auf das nächste Tasting mit dir.</p>
        </motion.div>
      </div>
    </TastingLayout>
  );
}
